import type {
  GetCustomerContactsRequest,
  GetCustomerContactsResponse,
  DeleteCustomerContactRequest,
} from "./customerContacts";
import type {
  GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest,
  GetMatchingProductsByUkdIdRequest,
} from "./ukdProducts";
import type { GetCompaniesWithCertifiedProductsWithFilteringRequest } from "./ukdCompanies";
import type { AddOrModifyContactRequest } from "@/Models/Requests/AddOrModifyContactRequest";
import type { CompanySummary } from "@/Domain/CompanySummary";
import type { NameValue } from "@/Domain/NameValue";
import type { Facility } from "@/Domain/Facility";
import type { IdAndName } from "@/Domain/IdAndName";
import type { CoPackerFacilities } from "@/Domain/CoPackerFacilities";
import type { CoPackerFacilitiesCategory } from "@/Domain/CoPackerFacilitiesCategory";
import type { CoPackerFacilitiesLocation } from "@/Domain/CoPackerFacilitiesLocation";
import type { ProductDisplay } from "@/Domain/ProductDisplay";
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import type { CompanyDisplay } from "@/Domain/CompanyDisplay";
import type {
  GetCompanyTbRequest,
  GetProductDisplayByCriteriaWithFilteringRequest,
  GetAuthorizedCertifiedOrPendingOwnsRequest,
  GetProductIdsByLabelRequest,
  ProductWithSelection,
} from "./kosherCertificates";
import type { CompanyTb } from "@/Domain/CompanyTb";
import type { Aging } from "@/Domain/Aging";
import type {
  GetAgingCustomerBalanceRequest,
  GetFinancialTransactionsWithFilteringRequest,
  FinancialTransactionWithSelection,
  GetFinancialPaymentsRequest,
  AddAchAuthTokenRequest,
  AchPayRequest,
  SensePassChargeRequest,
  DeleteSensePassPaymentMethodRequest,
} from "./financials";
import type { FinancialPayment } from "@/Domain/FinancialPayment";
import type { AchAuthToken } from "@/Domain/AchAuthToken";
import type { SensePassSavedPaymentMethod } from "@/Domain/SensePassSavedPaymentMethod";
import type {
  FindUsersByCompidWithFilteringRequest,
  GetUserEmailRequest,
  GetUserPlantsPermissionsRequest,
} from "./userLogin";
import type { MemberShipUserPlus } from "@/Domain/MemberShipUserPlus";
import type { UserPlantsPermissions } from "@/Domain/UserPlantsPermissions";
import type { SaveUserRequest } from "@/Domain/SaveUserRequest";
import type {
  GetProductDisplayScheduleBWithFilteringRequest,
  GetAllMatchingProductsInCartSubApplicationAndUserLoginCountRequest,
  ScheduleBDownloadPdfRequest,
  ScheduleBDownloadExcelRequest,
  AddProductToCartRequest,
  GetBrandsRequest,
  SaveProductToAddCartRequest,
  GetAllMatchingProductsInCartSubApplicationRequest,
  UpdateProductTerminationDateRequest,
  DeleteProductFromCartRequest,
  DeleteProductFromAddCartRequest,
  DeleteAllFromCartRequest,
  IsSubUserRequest,
  SubmitProductsCartRequest,
  ExportScheduleBHistoryRequest,
} from "./scheduleB";
import type {
  GetIngredientDisplayScheduleAWithFilteringRequest,
  ModifyRmcRequest,
  IngredientWithSelection,
  GetIngCartCountRequest,
  CopyOrDeleteExistingIngredientRequest,
  ScheduleADownloadPdfRequest,
  ScheduleADownloadExcelRequest,
  ExportScheduleAHistoryRequest,
  CopyIngredientsToCartRequest,
  DeleteIngredientFromCartRequest,
  SubmitIngredientsCartRequest,
  IngredientSourcesRequest,
  DeleteIngredientAttachmentRequest,
} from "./scheduleA";
import type { BrandNameAndCompanyIdAndName } from "@/Domain/BrandNameAndCompanyIdAndName";
import type { UploadApplicationFilesRequest } from "@/Domain/UploadApplicationFilesRequest";
import type { ProductDisplayInCart } from "@/Domain/ProductDisplayInCart";
import type { Attachment } from "@/Domain/Attachment";
import type { WasherFacilities } from "@/Domain/WasherFacilities";
import type {
  GetRfrAssigmentsWithFilteringRequest,
  EmailIngredientandProductListsRequest,
  AssignmentWithSelection,
  UpdateVisitPlannedDateRequest,
  GetPlantCompaniesWithFilteringRequest,
} from "./rfrAssignments";
import type { SendRfrEmailRequest } from "@/Domain/SendRfrEmailRequest";
import type { DownloadAssignmentSchedulesRequest } from "@/Domain/DownloadAssignmentSchedulesRequest";
import type {
  GetSubmissionsWithFilteringRequest,
  GetProductSubmissionDetailsByJobIdRequest,
  GetIngredientSubmissionDetailsByJobIdRequest,
  GetAssignmentPersonJobTbsRequest,
  SaveWorkflowDetailsRequest,
  WorkflowCheckOutJobRequest,
  GetWorkflowAttachmentsRequest,
  GetSubmissionDetailsAttachmentRequest,
} from "./submissionsOverview";
import type { SubmissionsTable } from "@/Domain/SubmissionsTable";
import type { ProductSubmissionDetail } from "@/Domain/ProductSubmissionDetail";
import type { IngredientSubmissionDetail } from "@/Domain/IngredientSubmissionDetail";
import type {
  BaseRequest,
  GetCompanyDenialsRequest,
  GetCompanySummaryRequest,
} from "./shared";
import type { IngCartCounts } from "@/Domain/IngCartCounts";
import type { IngredientDisplayInCart } from "@/Domain/IngredientDisplayInCart";
import type { SaveIngredientToCartRequest } from "@/Domain/SaveIngredientToCartRequest";
import type { DateLetterHeaderAndCount } from "@/Domain/DateLetterHeaderAndCount";
import type { ThrowExceptionRequest } from "./home";
import type { NewsItem } from "@/Domain/NewsItem";
import type {
  GetWorkflowAttachmentFileRequest,
  GetWorkflowsWithFilteringRequest,
  SetWorkflowPrivateCommentRequest,
  SetWorkflowUrgentStatusRequest,
} from "./workflowOverview";
import type { WfSubmissionProductAndIngredient } from "@/Domain/WfSubmissionProductAndIngredient";
import type {
  AddCoPackerFacilityRequest,
  DeleteCoPackerFacilityRequest,
  GetCompanyContactsRequest,
  UpdateCoPackerFacilityRequest,
} from "./copackerfacilities";
import type { GetTrademarkFileRequest } from "./trademarks";
import type { CompanycontactsTb } from "@/Domain/CompanycontactsTb";
import type { DeleteUserRequest } from "@/Domain/DeleteUserRequest";
import type {
  GetEirVisitsForRfrWithFilteringRequest,
  GetVisitsForRcWithFilteringRequest,
  GetVisitsOverviewForRcWithFilteringRequest,
  VisitDisplayExtended,
} from "./rfrVisitsSearch";
import type { GetPlantHistoryWithFilteringRequest } from "./plantHistory";
import type { PlantHistoryDisplay } from "@/Domain/PlantHistoryDisplay";
import type { PlantDisplay } from "@/Domain/PlantDisplay";
import type { CreateCrmInteractionRequest } from "@/Domain/CreateCrmInteractionRequest";
import type { MiniCrmInteraction } from "@/Domain/MiniCrmInteraction";
import type { UpdateCrmInteractionRequest } from "@/Domain/UpdateCrmInteractionRequest";
import type {
  GetCrmInteractionsRequest,
  GetRcCompanyListWithFilteringRequest,
} from "./miniCrm";
import type { MiniCrmCompany } from "@/Domain/MiniCrmCompany";
import type { MiniCrmRenewal } from "@/Domain/MiniCrmRenewal";
import type { MiniCrmPerson } from "@/Domain/MiniCrmPerson";
import type {
  DownloadChometzAttachmentRequest,
  GetChometzAttachmentsRequest,
  GetChometzSubmissionsWithFilteringRequest,
  GetRcChometzCompaniesWithFilteringRequest,
} from "./chomatzSales";
import type { ChometzCompany } from "@/Domain/ChometzCompany";
import type { SendChometzCompanyEmailRequest } from "@/Models/Requests/SendChometzCompanyEmailRequest";
import type { SubmitChometzSaleRequest } from "@/Models/Requests/SubmitChometzSaleRequest";
import type { PersonJobTb } from "@/Domain/PersonJobTb";
import type { ChometzSubmission } from "@/Domain/ChometzSubmission";
import type { InspectionVisitOtherCompany } from "@/Domain/InspectionVisitOtherCompany";
import type { CartDisplayReport } from "@/Domain/CartDisplayReport";
import type { SharePointAttachment } from "@/Domain/SharePointAttachment";
import type { SendWorkflowEmailRequest } from "@/Domain/SendWorkflowEmailRequest";
import type {
  GetWfProductSubmissionRequest,
  GetWfProductSubmissionLineItemsRequest,
} from "./workflowProductDetails";
import type { UpdateIngredientSubmissionApprovalRequest } from "@/Domain/UpdateIngredientSubmissionApprovalRequest";
import type { UpdateTripVisitsRequest } from "@/Domain/UpdateTripVisitsRequest";
import type { AssignmentPersonJobTb } from "@/Domain/AssignmentPersonJobTb";
import type { LocPrintRequest } from "@/Domain/LocPrintRequest";
import type { GenerateLocsForPickupRequest } from "@/Models/Requests/GenerateLocsForPickupRequest";
import type { LocPrintUkdSingleProductRequest } from "@/Models/Requests/LocPrintUkdSingleProductRequest";
import type { GetFacilityContactsInPlantRequest } from "./visitInspesctionForm";
import type { InspectionFacilityContact } from "@/Domain/InspectionFacilityContact";
import type { SubmitRfrVisitInspection } from "@/Models/Requests/SubmitRfrVisitInspection";
import type { EirSharePointAttachment } from "@/Domain/EirSharePointAttachment";
import type { DeleteSavedEirDocRequest } from "@/Models/Requests/DeleteSavedEirDocRequest";
import type { EirFormData } from "@/Models/EirFormData";
import type { UploadVisitInspectionFilesRequest } from "@/Models/Requests/UploadVisitInspectionFilesRequest";
import type { UpdateVisitDate } from "@/Models/Requests/UpdateVisitDate";
import type { ModifyReport } from "@/Models/Requests/ModifyReport";
import type { ModifyRfrVisitInspection } from "@/Models/Requests/ModifyRfrVisitInspection";
import type { InspectionRfrPlant } from "@/Domain/InspectionRfrPlant";
import type { InspectionCompanyByPlant } from "@/Domain/InspectionCompanyByPlant";
import type { EirRcPerson } from "@/Domain/EirRcPerson";
import type { CompanyRcInfo } from "@/Domain/CompanyRcInfo";
import type { CreateVisitNoPlantId } from "@/Models/Requests/CreateVisitNoPlantId";
import type { CreateVisit } from "@/Models/Requests/CreateVisit";
import type { InspectionRfrTeamMember } from "@/Domain/InspectionRfrTeamMember";
import type { EnumJobFunction } from "@/Domain/EnumJobFunction";
import type { OperationResult } from "@/Models/OperatiorResult";
import type { SharepointUploadAttachmentsResult } from "@/Models/Results/SharepointUploadAttachmentsResult";
import type { CancelVisitRequest } from "@/Models/Requests/CancelVisitRequest";
import type { ProductSubmissionLineItemDisplay } from "@/Domain/ProductSubmissionLineItemDisplay";
import type { GetRcRfrContactsWithFilteringRequest } from "./rcRfrContacts";
import type { Contact } from "@/Domain/Contact";
import type { SubmissionStatus } from "@/Domain/SubmissionStatus";
import type { UpdateIngredientSubmissionStatusRequest } from "@/Models/Requests/UpdateIngredientSubmissionStatusRequest";
import type { WfIngredientSubmissionDisplay } from "@/Domain/WfIngredientSubmissionDisplay";
import type { WfProductSubmissionDisplay } from "@/Domain/WfProductSubmissionDisplay";
import type { TripInfoReport } from "@/Domain/TripInfoReport";
import type { TripLength } from "@/Domain/TripLength";
import type { TripExpensesSummaryDisplay } from "@/Domain/TripExpensesSummaryDisplay";
import type { AddOrModifyCustomerContactRequest } from "@/Models/Requests/AddOrModifyCustomerContactRequest";
import { setCookie } from "@/shared/helpers/cookieHelper";
import { CookieTypes } from "@/Models/CookieTypes";
import type { SetNoChometzRequest } from "@/Models/Requests/SetNoChometzRequest";
import type { ReviewChometzSaleResponseRequest } from "@/Models/Requests/ReviewChometzSaleResponseRequest";
import type { DeleteChometzAttachmentRequest } from "@/Models/Requests/DeleteChometzAttachmentRequest";
import type { TripVisitDisplay } from "@/Domain/TripVisitDisplay";
import type { UpdateTripExpenseSummaryRequest } from "@/Models/Requests/UpdateTripExpenseSummaryRequest";
import type { TripExpenseSummary } from "@/Domain/TripExpenseSummary";
import type { SaveTripFilesRequest } from "@/Models/Requests/SaveTripFilesRequest";
import type { SubmitTripRequest } from "@/Models/Requests/SubmitTripRequest";
import { ENV_KEYS, getEnvValue } from "@/shared/helpers/envHelper";
import type { EnumVisitType } from "@/Domain/EnumVisitType";
import type { VisitDisplay } from "@/Domain/VisitDisplay";
import type { UserActivity } from "@/Domain/UserActivity";
import type { VisitInspectionAdditionalRequirementsDisplay } from "@/Domain/VisitInspectionAdditionalRequirementsDisplay";
import type { PlantVisitDetailsDisplay } from "./plantVisitDetails";
import cognitoAuthManager from "@/shared/access/CognitoAuth";

export type SuccessResponse<T> = {
  success: true;
  response: T;
};

export type ErrorResponse = {
  success: false;
  error: Error;
  // message to display to user
  message?: string;
};

export type GetBasicRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
};
export type GetHelpFilePathRequest = BaseRequest & {
  pageName: string;
  mode: string;
  type: string;
  subType: string;
};
export type GetHelpFileRequest = BaseRequest & {
  filePath: string;
};

export type OwnsPerLabelRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  labelType: string | undefined; // semicolon separated list of label types
};

export type SendEmailWithAttachmentRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  message: string | undefined;
  objectKey: string | undefined;
};
export type GeneratePreSignedUrlRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  objectKey: string;
  mimeType: string;
};
export type GetMatchingCompaniesNameValueRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  rcEmail: string;
  startsWith: string;
  includePassword: boolean;
};
export type GetFacilitiesRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  includeMainOffice: boolean;
  subApplication: number;
  contactId: number;
  includeSpecial: boolean;
};
export type GetCoPackerFacilitiesRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  searchTerms: string;
  filterItems: string;
  fields: string | null;
  operators: string | null;
  values: string | null;
  skip: number | null;
  take: number | null;
};

export type GetCoPackerFacilitiesResponse = {
  TotalItemsInList: number;
  ListItems: CoPackerFacilities[];
};

export type GetCopackerCategoriesRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  skip: number | null;
  take: number | null;
};

export type GetCopackerLocationsRequest = {
  userLogin: string | undefined;
  companyLogin: string | undefined;
  skip: number | null;
  take: number | null;
};

export type Response<T> = SuccessResponse<T> | ErrorResponse;

// API is a huge composite type of comma separated types. EG: export type API { key1:string, key2:number, ...} except that, instead of simple types like string and number, we define more complex types.
//  Each such type always has a first property named "request" followed by a property named "response"
export type API = {
  OwnsPerLabel: {
    request: OwnsPerLabelRequest;
    response: IdAndName[];
  };
  SendEmailWithAttachment: {
    request: SendEmailWithAttachmentRequest;
    response: string;
  };
  GeneratePreSignedUrl: {
    request: GeneratePreSignedUrlRequest;
    response: string;
  };
  GetShutdownMessage: {
    request: {};
    response: string;
  };
  GetHelpFilePath: {
    request: GetHelpFilePathRequest;
    response: string;
  };
  GetHelpFile: {
    request: GetHelpFileRequest;
    response: void;
  };
  GetMatchingCompaniesPlantNameCertifiedAndPendingOnly: {
    request: GetMatchingCompaniesNameValueRequest;
    response: NameValue[];
  };
  GetRolesForUser: {
    request: {};
    response: string[];
  };
  GetCustomerContacts: {
    request: GetCustomerContactsRequest;
    response: GetCustomerContactsResponse;
  };
  GetCompanySummary: {
    request: GetCompanySummaryRequest;
    response: CompanySummary;
  };
  GetFacilities: {
    request: GetFacilitiesRequest;
    response: Facility[];
  };
  AddOrModifyCustomerContact: {
    request: AddOrModifyCustomerContactRequest;
    response: string;
  };
  DeleteCustomerContact: {
    request: DeleteCustomerContactRequest;
    response: string;
  };
  AddOrModifyContact: {
    request: AddOrModifyContactRequest;
    response: number;
  };
  GetCoPackerFacilities: {
    request: GetCoPackerFacilitiesRequest;
    response: GetCoPackerFacilitiesResponse;
  };
  GetCoPackerFacilitiesCategories: {
    request: GetCopackerCategoriesRequest;
    response: CoPackerFacilitiesCategory[];
  };
  GetCoPackerFacilitiesLocations: {
    request: GetCopackerLocationsRequest;
    response: CoPackerFacilitiesLocation[];
  };
  GetMatchingProductsOnUkdOnSingleSearchFieldWithFiltering: {
    request: GetMatchingProductsOnUkdOnSingleSearchFieldWithFilteringRequest;
    response: {
      $id: string;
      ListItems: OuKashUnionUkdProducts[];
      TotalItemsInList: number;
    };
  };
  GetMatchingProductsByUkdId: {
    request: GetMatchingProductsByUkdIdRequest;
    response: {
      $id: string;
      ListItems: ProductDisplay[];
      TotalItemsInList: number;
    };
  };
  GetCompaniesWithCertifiedProductsWithFiltering: {
    request: GetCompaniesWithCertifiedProductsWithFilteringRequest;
    response: {
      $id: string;
      ListItems: CompanyDisplay[];
      TotalItemsInList: number;
    };
  };
  GetCompanyTb: {
    request: GetCompanyTbRequest;
    response: CompanyTb;
  };
  GetProductDisplayByCriteriaWithFiltering: {
    request: GetProductDisplayByCriteriaWithFilteringRequest;
    response: {
      $id: string;
      ListItems: ProductWithSelection[];
      TotalItemsInList: number;
    };
  };
  GetProductDisplayByCriteriaWithFilteringValuesForFieldOnly: {
    request: GetProductDisplayByCriteriaWithFilteringRequest;
    response: {
      $id: string;
      ListItems: string[];
      TotalItemsInList: number;
    };
  };
  GetAuthorizedCertifiedOrPendingOwns: {
    request: GetAuthorizedCertifiedOrPendingOwnsRequest;
    response: IdAndName[];
  };
  GetProductIdsByLabel: {
    request: GetProductIdsByLabelRequest;
    response: string[];
  };
  GenerateLocsForPickup: {
    request: GenerateLocsForPickupRequest;
    response: string;
  };
  GetPropertyValues: {
    request: {
      userLogin: string;
      companyLogin: string;
      propertyName: string;
    };
    response: string[];
  };
  SetPropertyValues: {
    request: {
      userLogin: string;
      companyLogin: string;
      propertyName: string;
      propertyValue: string;
    };
    response: void;
  };
  GetAgingCustomerBalance: {
    request: GetAgingCustomerBalanceRequest;
    response: {
      $id: string;
      ListItems: Aging[];
      TotalItemsInList: number;
    };
  };
  GetFinancialTransactionsWithFiltering: {
    request: GetFinancialTransactionsWithFilteringRequest;
    response: {
      $id: string;
      ListItems: FinancialTransactionWithSelection[];
      TotalItemsInList: number;
    };
  };
  GetFinancialPayments: {
    request: GetFinancialPaymentsRequest;
    response: FinancialPayment[];
  };

  AddAchAuthToken: {
    request: AddAchAuthTokenRequest;
    response: boolean;
  };
  AchPay: {
    request: AchPayRequest;
    response: boolean;
  };
  GetUserEmail: {
    request: GetUserEmailRequest;
    response: string;
  };
  GetAchAuthTokens: {
    request: { userLogin: string; companyLogin: string };
    response: AchAuthToken[];
  };
  FindUsersByCompidWithFiltering: {
    request: FindUsersByCompidWithFilteringRequest;
    response: {
      $id: string;
      ListItems: MemberShipUserPlus[];
      TotalItemsInList: number;
    };
  };
  GetProductDisplayScheduleBWithFiltering: {
    request: GetProductDisplayScheduleBWithFilteringRequest;
    response: {
      $id: string;
      ListItems: ProductWithSelection[];
      TotalItemsInList: number;
    };
  };
  GetUserPlantsPermissions: {
    request: GetUserPlantsPermissionsRequest;
    response: UserPlantsPermissions[];
  };
  VueGetAllMatchingProductsInCartSubApplicationAndUserLoginCount: {
    request: GetAllMatchingProductsInCartSubApplicationAndUserLoginCountRequest;
    response: number;
  };
  ScheduleBDownloadPdf: {
    request: ScheduleBDownloadPdfRequest;
    response: string;
  };
  ScheduleBDownloadExcel: {
    request: ScheduleBDownloadExcelRequest;
    response: string;
  };
  GetTrademarkFile: {
    request: GetTrademarkFileRequest;
    response: string;
  };
  VueAddProductToCart: {
    request: AddProductToCartRequest;
    response: number;
  };
  VueGetCategories: {
    request: {};
    response: { Item1: string; Item2: string }[];
  };
  VueGetBrands: {
    request: GetBrandsRequest;
    response: BrandNameAndCompanyIdAndName[];
  };
  VueSaveProductToCart: {
    request: SaveProductToAddCartRequest;
    response: string;
  };
  UploadApplicationFiles: {
    request: UploadApplicationFilesRequest;
    response: string;
  };
  VueGetAllMatchingProductsIncartSubApplication: {
    request: GetAllMatchingProductsInCartSubApplicationRequest;
    response: ProductDisplayInCart[];
  };
  VueUpdateProductTerminationDate: {
    request: UpdateProductTerminationDateRequest;
    response: boolean;
  };
  VueDeleteProductFromCart: {
    request: DeleteProductFromCartRequest;
    response: boolean;
  };
  VueDeleteProductFromAddCart: {
    request: DeleteProductFromAddCartRequest;
    response: boolean;
  };
  VueDeleteAllFromCart: {
    request: DeleteAllFromCartRequest;
    response: boolean;
  };
  GetHolidayMessage: {
    request: {};
    response: string;
  };
  VueSubmitProductsCart: {
    request: SubmitProductsCartRequest;
    response: string;
  };
  GetAttachmentsForProduct: {
    request: { productCartId: number };
    response: Attachment[];
  };
  DeleteProductAttachment: {
    request: { fullPathName: string };
    response: boolean;
  };
  RfrAssignmentsWithTotal: {
    request: { userLogin: string };
    response: {
      $id: string;
      ListItems: AssignmentWithSelection[];
      TotalItemsInList: number;
    };
  };
  GetRfrAssigmentsWithFiltering: {
    request: GetRfrAssigmentsWithFilteringRequest;
    response: {
      $id: string;
      ListItems: AssignmentWithSelection[];
      TotalItemsInList: number;
    };
  };
  UpdateVisitPlannedDate: {
    request: UpdateVisitPlannedDateRequest;
    response: boolean;
  };
  SaveUser: {
    request: SaveUserRequest;
    response: string;
  };
  DeleteUser: {
    request: DeleteUserRequest;
    response: string;
  };
  ValidateUser: {
    // cant' stick to convention and name it GetRcRfrOtherLogin because it is already named this way and used by other code
    request: { userName: string; password: string | undefined };
    response: string;
  };
  IsSubUser: {
    request: IsSubUserRequest;
    response: boolean;
  };
  GetWasherMap: {
    request: {};
    response: WasherFacilities;
  };
  GetCompanyDenials: {
    request: GetCompanyDenialsRequest;
    response: string;
  };
  VueLocPrint: {
    request: LocPrintRequest;
    response: void;
  };
  LocPrintUkdSingleProduct: {
    request: LocPrintUkdSingleProductRequest;
    response: void;
  };
  SendRfrEmail: {
    request: SendRfrEmailRequest;
    response: string;
  };
  SendDashboardEmail: {
    request: SendRfrEmailRequest;
    response: string;
  };
  GetRfrAssignmentOwns: {
    request: BaseRequest;
    response: { Item1: number; Item2: string }[];
  };
  EmailIngredientandProductLists: {
    request: EmailIngredientandProductListsRequest;
    response: boolean;
  };
  DownloadAssignmentSchedules: {
    request: DownloadAssignmentSchedulesRequest;
    response: void;
  };
  GetSubmissionsWithFiltering: {
    request: GetSubmissionsWithFilteringRequest;
    response: {
      $id: string;
      ListItems: SubmissionsTable[];
      TotalItemsInList: number;
    };
  };
  GetProductSubmissionDetails: {
    request: GetProductSubmissionDetailsByJobIdRequest;
    response: ProductSubmissionDetail[];
  };
  GetIngredientSubmissionDetails: {
    request: GetIngredientSubmissionDetailsByJobIdRequest;
    response: IngredientSubmissionDetail[];
  };
  VueGetWfIngredientSubmission: {
    request: { id: number };
    response: WfIngredientSubmissionDisplay;
  };
  VueGetWfProductSubmission: {
    request: GetWfProductSubmissionRequest;
    response: WfProductSubmissionDisplay;
  };
  GetWfProductSubmissionLineItems: {
    request: GetWfProductSubmissionLineItemsRequest;
    response: ProductSubmissionLineItemDisplay[];
  };
  GetAssignmentPersonJobTbs: {
    request: GetAssignmentPersonJobTbsRequest;
    response: AssignmentPersonJobTb[];
  };
  GetIngredientDisplayScheduleAWithFiltering: {
    request: GetIngredientDisplayScheduleAWithFilteringRequest;
    response: {
      $id: string;
      ListItems: IngredientWithSelection[];
      TotalItemsInList: number;
    };
  };
  VueModifyRmc: {
    request: ModifyRmcRequest;
    response: boolean;
  };
  VueGetIngCartCount: {
    request: GetIngCartCountRequest;
    response: IngCartCounts;
  };
  VueCopyOrDeleteExistingIngredient: {
    request: CopyOrDeleteExistingIngredientRequest;
    response: number;
  };
  ScheduleADownloadPdf: {
    request: ScheduleADownloadPdfRequest;
    response: string;
  };
  ScheduleADownloadExcel: {
    request: ScheduleADownloadExcelRequest;
    response: string;
  };
  ExportScheduleAHistory: {
    request: ExportScheduleAHistoryRequest;
    response: string;
  };
  ExportScheduleBHistory: {
    request: ExportScheduleBHistoryRequest;
    response: string;
  };
  CopyIngredientsToCart: {
    request: CopyIngredientsToCartRequest;
    response: { Item1: number; Item2: string };
  };
  VueGetAllMatchingIngredientsIncartSubApplicationAndUserLogin: {
    request: GetAllMatchingProductsInCartSubApplicationAndUserLoginCountRequest;
    response: IngredientDisplayInCart[];
  };
  VueDeleteIngredientFromCart: {
    request: DeleteIngredientFromCartRequest;
    response: boolean;
  };
  DeleteAllIngredientsFromCart: {
    request: DeleteAllFromCartRequest;
    response: boolean;
  };
  VueSubmitIngredientsCart: {
    request: SubmitIngredientsCartRequest;
    response: string;
  };
  IngredientSources: {
    request: IngredientSourcesRequest;
    response: IdAndName[];
  };
  VueSaveIngredientToCart: {
    request: SaveIngredientToCartRequest;
    response: string;
  };
  GetAttachmentsForIngredient: {
    request: { ingredientCartId: number };
    response: Attachment[];
  };
  GetGpcPlantId: {
    request: { userLogin: string };
    response: string;
  };
  DeleteIngredientAttachment: {
    request: DeleteIngredientAttachmentRequest;
    response: boolean;
  };
  GetSensePassSavedPaymentMethods: {
    request: { userLogin: string; companyLogin: string };
    response: SensePassSavedPaymentMethod[];
  };
  DeleteSensePassPaymentMethod: {
    request: DeleteSensePassPaymentMethodRequest;
    response: boolean;
  };
  GetSensePassCcTransactionNumber: {
    request: { userLogin: string; companyLogin: string };
    response: string;
  };
  SensePassCharge: {
    request: SensePassChargeRequest;
    response: boolean;
  };
  GetCcServiceFee: {
    request: { userLogin: string; companyLogin: string };
    response: number;
  };
  IsConsoleDebug: {
    request: {};
    response: boolean;
  };
  GetWorkflowsWithFiltering: {
    request: GetWorkflowsWithFilteringRequest;
    response: {
      $id: string;
      ListItems: WfSubmissionProductAndIngredient[];
      TotalItemsInList: number;
    };
  };
  SetWorkflowUrgentStatus: {
    request: SetWorkflowUrgentStatusRequest;
    response: boolean;
  };
  SetWorkflowPrivateComment: {
    request: SetWorkflowPrivateCommentRequest;
    response: boolean;
  };
  SaveWorkflowDetails: {
    request: SaveWorkflowDetailsRequest;
    response: boolean;
  };
  WorkflowCheckOutJob: {
    request: WorkflowCheckOutJobRequest;
    response: boolean;
  };
  GetWorkflowAttachments: {
    request: GetWorkflowAttachmentsRequest;
    response: SharePointAttachment[];
  };
  GetWorkflowAttachmentFile: {
    request: GetWorkflowAttachmentFileRequest;
    response: void;
  };
  GetLocPickup: {
    request: { companyLogin: string };
    response: DateLetterHeaderAndCount[];
  };
  DownloadLocByUkdId: {
    request: { companyLogin: string; ukdId: string };
    response: void;
  };
  GetProductDisplayCount: {
    request: BaseRequest;
    response: number;
  };
  GetIngredientDisplayCount: {
    request: BaseRequest;
    response: number;
  };
  GetProductDisplayScheduleBCount: {
    request: BaseRequest;
    response: number;
  };
  GetNewsItems: {
    request: {};
    response: NewsItem[];
  };
  ThrowException: {
    request: ThrowExceptionRequest;
    response: string;
  };
  DefaultInitialize: {
    request: {};
    response: void;
  };
  GetAvailableCoPackerCompanies: {
    request: BaseRequest;
    response: NameValue[];
  };
  AddCoPackerFacility: {
    request: AddCoPackerFacilityRequest;
    response: CoPackerFacilities;
  };
  UpdateCoPackerFacility: {
    request: UpdateCoPackerFacilityRequest;
    response: CoPackerFacilities;
  };
  GetCompanyContacts: {
    request: GetCompanyContactsRequest;
    response: CompanycontactsTb[];
  };
  DeleteCoPackerFacility: {
    request: DeleteCoPackerFacilityRequest;
    response: boolean;
  };
  IsCopackerManagementAllowed: {
    request: BaseRequest;
    response: boolean;
  };
  GetCurrentPersonJob: {
    request: BaseRequest;
    response: PersonJobTb;
  };
  GetSubmissionDetailsAttachment: {
    request: GetSubmissionDetailsAttachmentRequest;
    response: void;
  };
  GetRcCompanyListWithFiltering: {
    request: GetRcCompanyListWithFilteringRequest;
    response: {
      ListItems: MiniCrmCompany[];
      TotalItemsInList: number;
    };
  };
  CreateCrmInteraction: {
    request: CreateCrmInteractionRequest;
    response: MiniCrmInteraction;
  };
  UpdateCrmInteraction: {
    request: UpdateCrmInteractionRequest;
    response: MiniCrmInteraction;
  };
  GetCrmInteractions: {
    request: GetCrmInteractionsRequest;
    response: {
      ListItems: MiniCrmInteraction[];
      TotalItemsInList: number;
    };
  };
  GetRcRenewalData: {
    request: {
      userLogin: string;
      emulatedPersonJobId: number;
      ignoreYear: boolean;
    };
    response: MiniCrmRenewal;
  };
  GetCrmRcList: {
    request: { userLogin: string };
    response: MiniCrmPerson[];
  };
  GetRcChometzCompaniesPlantsWithFiltering: {
    request: GetRcChometzCompaniesWithFilteringRequest;
    response: {
      ListItems: ChometzCompany[];
      TotalItemsInList: number;
    };
  };
  SendChometzCompanyEmail: {
    request: SendChometzCompanyEmailRequest;
    response: boolean;
  };
  SubmitChometzSale: {
    request: SubmitChometzSaleRequest;
    response: boolean;
  };
  GetChometzSubmissionsWithFiltering: {
    request: GetChometzSubmissionsWithFilteringRequest;
    response: {
      ListItems: ChometzSubmission[];
      TotalItemsInList: number;
    };
  };
  vCartVisitsOtherCompanies: {
    request: { userName: string; visitId: number };
    response: InspectionVisitOtherCompany[];
  };
  GetEirDisplayReport: {
    request: { visitId: number };
    response: CartDisplayReport;
  };
  SendWorkflowEmail: {
    request: SendWorkflowEmailRequest;
    response: string;
  };
  IngredientSubmissionApprovalGet: {
    request: { userLogin: string; jobId: string };
    response: boolean;
  };
  UpdateIngredientSubmissionApproval: {
    request: UpdateIngredientSubmissionApprovalRequest;
    response: boolean;
  };
  GetEirVisitsForRfrWithFiltering: {
    request: GetEirVisitsForRfrWithFilteringRequest;
    response: {
      ListItems: VisitDisplayExtended[];
      TotalItemsInList: number;
    };
  };
  GetVisitsForRcWithFiltering: {
    request: GetVisitsForRcWithFilteringRequest;
    response: {
      ListItems: VisitDisplayExtended[];
      TotalItemsInList: number;
    };
  };
  GetVisitsOverviewForRcWithFiltering: {
    request: GetVisitsOverviewForRcWithFilteringRequest;
    response: {
      ListItems: VisitDisplay[];
      TotalItemsInList: number;
    };
  };
  GetVisitsOverviewForRcCount: {
    request: GetVisitsOverviewForRcWithFilteringRequest;
    response: number;
  };
  GetPlantHistoryWithFiltering: {
    request: GetPlantHistoryWithFilteringRequest;
    response: {
      ListItems: PlantHistoryDisplay[];
      TotalItemsInList: number;
    };
  };
  IsRfrTeamLeader: {
    request: BaseRequest;
    response: boolean;
  };
  GetTripId: {
    request: BaseRequest;
    response: number;
  };
  UpdateTripVisits: {
    request: UpdateTripVisitsRequest;
    response: boolean;
  };
  GetFacilityContactsInPlant: {
    request: GetFacilityContactsInPlantRequest;
    response: InspectionFacilityContact[];
  };
  SubmitRfrVisitInspection: {
    request: SubmitRfrVisitInspection;
    response: OperationResult<SharepointUploadAttachmentsResult>;
  };
  GetEirUploadedDocs: {
    request: { visitId: number };
    response: EirSharePointAttachment[];
  };
  GetSavedEirUploadedDocs: {
    request: { visitId: number };
    response: Attachment[];
  };
  GetSavedTripUploadedDocs: {
    request: { tripId: number };
    response: Attachment[];
  };
  DownloadEirDoc: {
    request: { fileUrl: string; fileName: string };
    response: void;
  };
  DeleteSavedEirDoc: {
    request: DeleteSavedEirDocRequest;
    response: boolean;
  };
  SaveEirFormData: {
    request: EirFormData;
    response: boolean;
  };
  SaveVisitInspectionFiles: {
    request: UploadVisitInspectionFilesRequest;
    response: boolean;
  };
  SaveTripFiles: {
    request: SaveTripFilesRequest;
    response: boolean;
  };
  GetSavedEirFormData: {
    request: { visitId: number };
    response: EirFormData;
  };
  vCartUpdateVisitDate: {
    request: UpdateVisitDate;
    response: boolean;
  };
  vCartModifyReport: {
    request: ModifyReport;
    response: number;
  };
  ModifyRfrVisitInspection: {
    request: ModifyRfrVisitInspection;
    response: boolean;
  };
  RfrPlantList: {
    request: { visitType: string };
    response: InspectionRfrPlant[];
  };
  GetPlant: {
    request: { plantId: number };
    response: PlantDisplay;
  };
  GetPlantVisitDetails: {
    request: { visitId: number };
    response: PlantVisitDetailsDisplay;
  };
  CompanyListbyPlantID: {
    request: { plantId: string };
    response: InspectionCompanyByPlant[];
  };
  GetStates: {
    request: {};
    response: { Item1: string; Item2: string }[];
  };
  CountryList: {
    request: {};
    response: string[];
  };
  GetEirRcList: {
    request: {};
    response: EirRcPerson[];
  };
  GetCompanyRcInfo: {
    request: { userLogin: string; compid: string };
    response: CompanyRcInfo;
  };
  CreateVisitNoPlantId: {
    request: CreateVisitNoPlantId;
    response: number;
  };
  CreateVisit: {
    request: CreateVisit;
    response: number;
  };
  GetRfrTeamMembers: {
    request: { visitId: number };
    response: InspectionRfrTeamMember[];
  };
  VueGetAssociatedPeopleCrm: {
    request: { userLogin: string; role: EnumJobFunction };
    response: MiniCrmPerson[];
  };
  VueGetAssociatedPeople: {
    request: { userLogin: string; role: EnumJobFunction };
    response: PersonJobTb[];
  };
  CancelVisit: {
    request: CancelVisitRequest;
    response: boolean;
  };
  ChometzAvailable: {
    request: {};
    response: boolean;
  };
  IsParentCompany: {
    request: { userName: string };
    response: boolean;
  };
  GetRcRfrContactsWithFiltering: {
    request: GetRcRfrContactsWithFilteringRequest;
    response: {
      ListItems: Contact[];
      TotalItemsInList: number;
    };
  };
  GetSubmissionStatusValues: {
    request: {};
    response: SubmissionStatus[];
  };
  UpdateIngredientSubmissionStatus: {
    request: UpdateIngredientSubmissionStatusRequest;
    response: boolean;
  };
  GetLocPickupUrl: {
    request: { companyLogin: string; locPickupKey: string };
    response: string;
  };
  GetAllowedAttachmentTypes: {
    request: {};
    response: string[];
  };
  GetMaxTotalUploadSize: {
    request: {};
    response: number;
  };
  GetPlantCompaniesWithFiltering: {
    request: GetPlantCompaniesWithFilteringRequest;
    response: {
      ListItems: CompanyDisplay[];
      TotalItemsInList: number;
    };
  };
  GetTripLength: {
    request: { tripId: number };
    response: TripLength;
  };
  GetTripInfoReports: {
    request: { tripId: number };
    response: TripInfoReport[];
  };
  GetEfrUploadedDocs: {
    request: { tripId: number };
    response: EirSharePointAttachment[];
  };
  DownloadEfrDoc: {
    request: { fileUrl: string; fileName: string };
    response: void;
  };
  GetTheUsersCompId: {
    request: { userName: string };
    response: string;
  };
  WasRemovedFromOuDirect: {
    request: {};
    response: boolean;
  };
  GetFormW9: {
    request: {};
    response: string;
  };
  SetNoChometz: {
    request: SetNoChometzRequest;
    response: boolean;
  };
  ReviewChometzSaleResponse: {
    request: ReviewChometzSaleResponseRequest;
    response: boolean;
  };
  GetTimeZones: {
    request: {};
    response: NameValue[];
  };
  GetCompanyJewishPlants: {
    request: { companyLogin: string };
    response: Facility[];
  };
  GetCurrentChometzSaleSeason: {
    request: {};
    response: number;
  };
  GetChometzSalePeriod: {
    request: { year: number };
    response: { Item1: Date; Item2: Date };
  };
  GetChometzAttachments: {
    request: GetChometzAttachmentsRequest;
    response: Attachment[];
  };
  DownloadChometzAttachment: {
    request: DownloadChometzAttachmentRequest;
    response: void;
  };
  DeleteChometzAttachment: {
    request: DeleteChometzAttachmentRequest;
    response: boolean;
  };
  CloseVisits: {
    request: { VisitIds: number[] };
    response: boolean;
  };
  GetMileageRateForRfr: {
    request: {};
    response: number | null;
  };
  GetTripVisits: {
    request: { tripId: number; newTrip: boolean };
    response: TripVisitDisplay[];
  };
  DeleteTripVisit: {
    request: { TripId: number; VisitId: number };
    response: boolean;
  };
  UpdateTripExpenseSummary: {
    request: UpdateTripExpenseSummaryRequest;
    response: boolean;
  };
  GetTripExpensesSummary: {
    request: { tripId: number; newTrip: boolean; caller?: string };
    response: TripExpensesSummaryDisplay;
  };
  GetCurrentTripVisits: {
    request: {};
    response: number[];
  };
  DeleteSavedTripFile: {
    request: { TripId: number; FileName: string };
    response: boolean;
  };
  SubmitTrip: {
    request: SubmitTripRequest;
    response: OperationResult<SharepointUploadAttachmentsResult>;
  };
  GetNoReceiptsCap: {
    request: {};
    response: number;
  };
  PrintInvoice: {
    request: { invoiceId: string };
    response: string;
  };
  GetMatchingVisit: {
    request: { plantId: number; type: EnumVisitType };
    response: VisitDisplay;
  };
  EmulatorPaymentsAllowed: {
    request: {};
    response: boolean;
  };
  Activity: {
    request: { ActivityId: string };
    response: UserActivity[];
  };
  GetDocFile: {
    request: { fileName: string };
    response: void;
  };
  GetVisitInspectionAdditionalRequirements: {
    request: { visitId: number };
    response: VisitInspectionAdditionalRequirementsDisplay;
  };
};

const fetchAsync = async <T extends keyof API>(
  input: RequestInfo,
  init?: RequestInit
) => {
  // Handle cancelation of fetch requests on page unload
  const controller = new AbortController();
  if (!init || !init.signal) {
    const signal = controller.signal;
    init = { ...init, signal };
  }
  init.headers = {
    ...init.headers,
    Authorization: `Bearer ${await cognitoAuthManager.getAccessToken()}`,
    FullReferer: window.location.href,
  };

  const beforeUnloadListener = () => {
    controller.abort();
  };

  try {
    window.addEventListener("beforeunload", beforeUnloadListener);

    setCookie(
      CookieTypes.Authorization,
      await cognitoAuthManager.getAccessToken() ?? "",
      60 * 60
    );

    const fetchResponse = await fetch(input, init);

    if (fetchResponse.redirected) {
      window.location.href = fetchResponse.url;
    }

    const json = await fetchResponse.json();

    if (!fetchResponse.ok) {
      return {
        success: false,
        error: new Error(json.toString()),
        message: json.toString(),
      } as ErrorResponse;
    }

    return {
      success: true,
      response: json,
    } as SuccessResponse<T>;
  } catch (error) {
    return {
      success: false,
      error,
    } as ErrorResponse;
  } finally {
    window.removeEventListener("beforeunload", beforeUnloadListener);
  }
};

const get = async <T extends keyof API>(
  endpoint: T,
  request: API[T]["request"],
  init?: RequestInit
): Promise<Response<API[T]["response"]>> => {
  const records: Record<string, string> = {};

  // union of all keys in the request object, whatever type it may be
  let k: keyof typeof request;
  for (k in request) {
    if (!Object.prototype.hasOwnProperty.call(request, k)) continue;
    const value = request[k] as unknown;
    records[k.toString()] = String(value);
  }
  return fetchAsync(
    `${getEnvValue(ENV_KEYS.ApiUrl)}/${endpoint}?` +
      new URLSearchParams(records),
    init
  );
};

const post = <T extends keyof API>(
  endpoint: T,
  request: API[T]["request"]
): Promise<Response<API[T]["response"]>> => {
  return fetchAsync(`${getEnvValue(ENV_KEYS.ApiUrl)}/${endpoint}`, {
    method: "POST",
    body: JSON.stringify(request),
  });
}; 

const fetchFileAsync = async <T extends keyof API>(
  input: RequestInfo,
  init?: RequestInit
) => {
  // Handle cancelation of fetch requests on page unload
  const controller = new AbortController();
  const signal = controller.signal;
  init = { ...init, signal };
  init.headers = {
    ...init.headers,
    Authorization: `Bearer ${await cognitoAuthManager.getAccessToken()}`,
    FullReferer: window.location.href,
  };

  const beforeUnloadListener = () => {
    controller.abort();
  };

  try {
    window.addEventListener("beforeunload", beforeUnloadListener);

    const fetchResponse = await fetch(input, init);

    if (!fetchResponse.ok) {
      const json = await fetchResponse.json();
      return {
        success: false,
        error: new Error(json.toString()),
        message: json.toString(),
      } as ErrorResponse;
    }

    const filename = fetchResponse
      .headers!.get("Content-Disposition")!
      .split("=")[1]
      .replaceAll('"', "")
      .trim();
    const blob = await fetchResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return {
      success: true,
      response: fetchResponse,
    } as SuccessResponse<any>;
  } catch (error) {
    return {
      success: false,
      error,
    } as ErrorResponse;
  } finally {
    window.removeEventListener("beforeunload", beforeUnloadListener);
  }
};

const getDownload = async <T extends keyof API>(
  endpoint: T,
  request: API[T]["request"]
): Promise<Response<API[T]["response"]>> => {
  const records: Record<string, string> = {};

  // union of all keys in the request object, whatever type it may be.
  let k: keyof typeof request;
  for (k in request) {
    if (!Object.prototype.hasOwnProperty.call(request, k)) continue;
    const value = request[k] as unknown;
    records[k.toString()] = String(value);
  }

  return fetchFileAsync(
    `${getEnvValue(ENV_KEYS.ApiUrl)}/${endpoint}?` +
      new URLSearchParams(records)
  );
};

const postDownload = <T extends keyof API>(
  endpoint: T,
  request: API[T]["request"]
): Promise<Response<API[T]["response"]>> => {
  return fetchFileAsync(`${getEnvValue(ENV_KEYS.ApiUrl)}/${endpoint}`, {
    method: "POST",
    body: JSON.stringify(request),
  });
};

export const api = { get, post, getDownload, postDownload };
